import React, { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Vehicle } from '@bringg/types';
import { InputNumber, Select } from '@bringg/react-components';
import { SortByStringWithNumber } from '@bringg-frontend/utils';

import { getDriverNotAvailableIcon, getVehicleNotAvailableIcon } from '../select-options-provider';
import { useStores } from '../../../../../recipes';
import { Option } from '../../../../../components';
import Driver from '../../../../../stores/drivers/domain-object/driver';
import { filterOption } from 'bringg-web/services/utils';

import './resources-actions.scss';

enum ResourceType {
	Vehicles = 'DELIVERY_BLOCKS.RESOURCES.VEHICLES',
	Drivers = 'DELIVERY_BLOCKS.RESOURCES.DRIVERS'
}

interface Props {
	capacity: {
		value: number;
		onCapacityChanged: (value: number) => void;
	};
	resources: {
		clearResources: () => void;
		isResourceChangeDisabled: () => boolean;
		length: number;
	};
	drivers: {
		drivers: Map<number, Driver>;
		addDriver: (driverId: number) => void;
		removeDriver: (driverId: number) => void;
		selectedDrivers: Set<number>;
	};
	vehicles: {
		vehicles: Map<number, Vehicle>;
		addVehicle: (vehicleId) => void;
		removeVehicle: (vehicleId) => void;
		selectedVehicles: Set<number>;
	};
	deliveryBlockId?: number;
	unavailableDriverIds: Set<number>;
	unavailableVehicleIds: Set<number>;
	unavailableTrailerIds: Set<number>;
}

const ResourcesActions = ({
	resources,
	drivers: propDrivers,
	vehicles: propVehicles,
	capacity: propCapacity,
	unavailableDriverIds,
	unavailableVehicleIds,
	unavailableTrailerIds
}: Props) => {
	const { t } = useTranslation();
	const { clearResources, isResourceChangeDisabled } = resources;
	const { vehicles, removeVehicle, addVehicle, selectedVehicles } = propVehicles;
	const { selectedDrivers, removeDriver, addDriver, drivers } = propDrivers;
	const capacity = propCapacity.value;

	const { merchantConfigurationsStore } = useStores();

	const isChangeDisabled = isResourceChangeDisabled();

	const driversSelect = useCallback(() => {
		const driversOptions = isChangeDisabled ? [] : SortByStringWithNumber(Array.from(drivers.values()), 'name');
		return (
			<Select
				className="resource-select"
				mode="multiple"
				onSelect={addDriver}
				onDeselect={removeDriver}
				value={Array.from(selectedDrivers)}
				getPopupContainer={triggerNode => triggerNode.parentElement}
				showArrow
				disabled={isChangeDisabled}
				blockBodyClicks
				showSearch
				filterOption={filterOption}
			>
				{driversOptions.map(driver => (
					<Option
						key={driver.id}
						value={driver.id}
						data-test-id={`${driver.id}-dropdown-option`}
						label={driver.name}
					>
						<span data-test-id="driver-name">{driver.name}</span>
						{getDriverNotAvailableIcon(
							driver,
							t('DELIVERY_BLOCKS.DRIVER_NOT_AVAILABLE_AT_THIS_TIME'),
							unavailableDriverIds
						)}
					</Option>
				))}
			</Select>
		);
	}, [unavailableDriverIds, capacity, selectedDrivers, selectedVehicles]);

	const vehiclesSelect = useCallback(() => {
		const vehiclesOptions = isChangeDisabled ? [] : SortByStringWithNumber(Array.from(vehicles.values()), 'name');

		return (
			<Select
				className="resource-select"
				mode="multiple"
				onSelect={addVehicle}
				onDeselect={removeVehicle}
				value={Array.from(selectedVehicles)}
				getPopupContainer={triggerNode => triggerNode.parentElement}
				showArrow
				disabled={isChangeDisabled}
				blockBodyClicks
				filterOption={filterOption}
				showSearch
			>
				{vehiclesOptions.map(vehicle => (
					<Option
						key={vehicle.id}
						value={vehicle.id}
						data-test-id={`${vehicle.id}-dropdown-option`}
						label={vehicle.name}
					>
						<span data-test-id="vehicle-name">{vehicle.name}</span>
						{getVehicleNotAvailableIcon(
							vehicle,
							t('DELIVERY_BLOCKS.VEHICLE_NOT_AVAILABLE_AT_THIS_TIME'),
							unavailableVehicleIds,
							unavailableTrailerIds
						)}
					</Option>
				))}
			</Select>
		);
	}, [unavailableVehicleIds, unavailableTrailerIds, capacity, selectedDrivers, selectedVehicles]);

	const resourceTypes = () => {
		if (merchantConfigurationsStore.configuration.enable_vehicles) {
			return Object.values(ResourceType);
		}
		return [ResourceType.Drivers];
	};

	const [resourceType, setResourceType] = useState<ResourceType>(resourceTypes()[0]);

	return (
		<div className="actions-container">
			<div className="capacity-container">
				<span className="capacity-title">{t('DELIVERY_BLOCKS.CAPACITY')}</span>
				<InputNumber
					value={capacity}
					min={resources.length}
					onChange={value => propCapacity.onCapacityChanged(+value)}
					className="capacity-input"
					data-test-id="capacity-input"
				/>
			</div>
			<div className="resources-container">
				<span className="resources-title">{t('DELIVERY_BLOCKS.RESOURCES.TITLE')}</span>
				<div className="resources-selectors">
					<Select
						onSelect={setResourceType}
						value={resourceType}
						className="resource-type-select"
						disabled={isChangeDisabled}
						getPopupContainer={triggerNode => triggerNode.parentElement}
						blockBodyClicks
					>
						{resourceTypes().map(option => (
							<Option id={option} key={option} value={option} data-test-id={`${option}-dropdown-option`}>
								<span className="resource-type-text">{t(option)}</span>
							</Option>
						))}
					</Select>
					{resourceType === ResourceType.Drivers && driversSelect()}
					{resourceType === ResourceType.Vehicles && vehiclesSelect()}
				</div>
			</div>
			<span className="clear-all" data-test-id="clear-all-resources" onClick={clearResources}>
				{t('DELIVERY_BLOCKS.RESOURCES.CLEAR_ALL')}
			</span>
		</div>
	);
};

export default ResourcesActions;
